import { formatISO } from "date-fns";
import {
    ACCOUNTING_REPORT_LABELS,
    AccountingReportType,
} from "@common/types/reports/report-type";
import { Entity } from "@common/types/entity";
import { ConsolidationReportContext } from "@components/accounting/consolidationReports/useConsolidationReportContext";
import {
    ConsolidationReportDestinationId,
    getConsolidationReportDestinations,
} from "@components/accounting/consolidationReports/consolidationReportDestinations";
import { SCHEDULES_REPORT_NAV_ENTRIES } from "@components/accounting/schedulesReport/schedulesReportCatalogEntries";
import {
    FORM_1099_NEC_REPORT_DESCRIPTION,
    FORM_1099_NEC_REPORT_LABEL,
} from "@components/accounting/form1099Nec/form1099NecFormat";
import { isEligibleReportingEntity } from "./reportingScope";

export type ReportingScope =
    | {
          kind: "consolidationGroup";
          id: string;
          label: string;
      }
    | {
          kind: "entity";
          id: number;
          label: string;
      };

interface ReportingCatalogItemBase {
    id: string;
    scope: ReportingScope;
    label: string;
    description: string;
}

export type ReportingCatalogItem =
    | (ReportingCatalogItemBase & {
          kind: "report";
          reportType?: AccountingReportType;
          url: string;
          catalogId?: string;
      })
    | (ReportingCatalogItemBase & {
          kind: "taxPackage";
      })
    | (ReportingCatalogItemBase & {
          kind: "reportPackage";
      });

type EntityReportQuery = "dateRange" | "entityIds" | "entityId";

interface EntityReportDescriptorBase {
    path: string;
    description: string;
    query: EntityReportQuery;
    label?: string;
    viewId?: string;
}

type EntityReportDescriptor = EntityReportDescriptorBase &
    (
        | { reportType: AccountingReportType; catalogId?: string }
        | { reportType?: undefined; catalogId: string; label: string }
    );

export const REPORTING_ENTITY_REPORTS: readonly EntityReportDescriptor[] = [
    {
        reportType: AccountingReportType.PROFIT_LOSS,
        path: "profit-loss",
        description: "Income and expenses over a selected period",
        query: "dateRange",
    },
    {
        reportType: AccountingReportType.BALANCE_SHEET,
        path: "balance-sheet",
        description: "Assets, liabilities, and equity at a selected date",
        query: "dateRange",
    },
    {
        reportType: AccountingReportType.GENERAL_LEDGER,
        path: "general-ledger",
        description: "Transactions by account over a selected period",
        query: "dateRange",
    },
    {
        reportType: AccountingReportType.TRIAL_BALANCE,
        path: "trial-balance",
        description: "Account balances with total debits and credits",
        query: "dateRange",
    },
    {
        reportType: AccountingReportType.EXPENSE_BY_VENDOR,
        path: "expenses-by-vendor",
        description: "Expenses by vendor over a selected period",
        query: "dateRange",
    },
    {
        reportType: AccountingReportType.CASH_FLOW_STATEMENT,
        path: "cash-flow-statement",
        description: "Cash inflows and outflows by business activity",
        query: "dateRange",
    },
];

export const REPORTING_AR_AGING_REPORTS: readonly EntityReportDescriptor[] = [
    {
        reportType: AccountingReportType.AR_AGING_SUMMARY,
        path: "ar-aging-summary",
        description:
            "Open invoices grouped by customer and aging bucket as of a given date",
        query: "entityIds",
    },
    {
        reportType: AccountingReportType.AR_AGING_DETAIL,
        path: "ar-aging-detail",
        description:
            "Open invoices that make up each aging bucket as of a given date",
        query: "entityIds",
    },
];

export const REPORTING_SCHEDULES_REPORTS: readonly EntityReportDescriptor[] =
    SCHEDULES_REPORT_NAV_ENTRIES.map((entry) => ({
        reportType: AccountingReportType.SCHEDULES,
        path: "schedules",
        catalogId: entry.id,
        label: entry.title,
        description: entry.description,
        query: "dateRange",
        viewId: entry.viewId,
    }));

export const CHECK_DETAIL_REPORT_DESCRIPTION =
    "Recorded checks and deposits by bank account over a selected period, with counterparty, account, and reconciliation status";

export const REPORTING_CHECK_DETAIL_REPORTS: readonly EntityReportDescriptor[] =
    [
        {
            reportType: AccountingReportType.CHECK_DETAIL,
            path: "check-detail",
            description: CHECK_DETAIL_REPORT_DESCRIPTION,
            query: "dateRange",
        },
    ];

export const REPORTING_FORM_1099_NEC_REPORTS: readonly EntityReportDescriptor[] =
    [
        {
            catalogId: "form-1099-nec",
            path: "1099-nec",
            label: FORM_1099_NEC_REPORT_LABEL,
            description: FORM_1099_NEC_REPORT_DESCRIPTION,
            query: "entityId",
        },
    ];

const TAX_PACKAGE_LABEL = "2025 Year-End Tax Package";
const TAX_PACKAGE_DESCRIPTION =
    "Tax Package includes a full-year General Ledger, Trial Balance, Profit and Loss Statement, and a Balance Sheet";
const REPORT_PACKAGE_LABEL = "Custom Report Package";
const REPORT_PACKAGE_DESCRIPTION =
    "Create a custom package with selected reports for your entities";

const REPORTING_DESTINATION_REPORT_TYPES: Partial<
    Record<ConsolidationReportDestinationId, AccountingReportType>
> = {
    profitLoss: AccountingReportType.PROFIT_LOSS,
    balanceSheet: AccountingReportType.BALANCE_SHEET,
    eliminationLedger: AccountingReportType.ELIMINATION_DETAIL,
};

export const entityScopeKey = (entityId: number): string =>
    `entity:${entityId}`;

export const consolidationScopeKey = (groupId: string): string =>
    `group:${groupId}`;

export const reportingScopeKey = (scope: ReportingScope): string =>
    scope.kind === "consolidationGroup"
        ? consolidationScopeKey(scope.id)
        : entityScopeKey(scope.id);

export function reportingCatalogReportGroup(item: ReportingCatalogItem): {
    key: string;
    label: string;
} {
    switch (item.kind) {
        case "report":
            if (item.catalogId) {
                return {
                    key: `report:${item.catalogId}`,
                    label: item.label,
                };
            }
            if (item.reportType === undefined) {
                return { key: `report:${item.id}`, label: item.label };
            }
            return {
                key: `report:${item.reportType}`,
                label: ACCOUNTING_REPORT_LABELS[item.reportType],
            };
        case "taxPackage":
            return { key: "tax-package", label: item.label };
        case "reportPackage":
            return { key: "report-package", label: item.label };
        default: {
            const unreachable: never = item;
            return unreachable;
        }
    }
}

function resolveEntityReportLabel(report: EntityReportDescriptor): string {
    if (report.label) {
        return report.label;
    }
    if (report.reportType === undefined) {
        return report.catalogId;
    }
    return ACCOUNTING_REPORT_LABELS[report.reportType];
}

function entityReportQuery({
    report,
    entityId,
    startDate,
    endDate,
}: {
    report: EntityReportDescriptor;
    entityId: number;
    startDate: Date;
    endDate: Date;
}): Record<string, string> {
    switch (report.query) {
        case "dateRange":
            return {
                startDate: formatISO(startDate, { representation: "date" }),
                endDate: formatISO(endDate, { representation: "date" }),
                entityId: entityId.toString(),
            };
        case "entityIds":
            return { entityIds: entityId.toString() };
        case "entityId":
            return { entityId: entityId.toString() };
        default: {
            const unreachable: never = report.query;
            return unreachable;
        }
    }
}

function buildEntityReportUrl({
    report,
    entityId,
    startDate,
    endDate,
}: {
    report: EntityReportDescriptor;
    entityId: number;
    startDate: Date;
    endDate: Date;
}): string {
    const params = new URLSearchParams(
        entityReportQuery({ report, entityId, startDate, endDate }),
    );
    if (report.viewId) {
        params.set("view", report.viewId);
    }
    return `/accounting/${report.path}?${params.toString()}`;
}

/**
 * Builds the flat, scope-grouped report catalog rendered by the Reporting page.
 * The consolidation group's reports come first (when a group exists), followed
 * by business entities in their existing order. Labels come from the canonical
 * `ACCOUNTING_REPORT_LABELS` and every URL is assembled here so callers never
 * hand-build report links. Tax package and custom report package rows are
 * actions for each business entity.
 */
export function buildReportingCatalog({
    entities,
    consolidationContext,
    startDate,
    endDate,
    entityReports = REPORTING_ENTITY_REPORTS,
}: {
    entities: readonly Entity[];
    consolidationContext?: ConsolidationReportContext;
    startDate: Date;
    endDate: Date;
    entityReports?: readonly EntityReportDescriptor[];
}): readonly ReportingCatalogItem[] {
    const items: ReportingCatalogItem[] = [];

    if (consolidationContext) {
        const scope: ReportingScope = {
            kind: "consolidationGroup",
            id: consolidationContext.group.id,
            label: consolidationContext.displayName,
        };
        for (const destination of getConsolidationReportDestinations({
            context: consolidationContext,
            startDate,
            endDate,
        })) {
            const reportType =
                REPORTING_DESTINATION_REPORT_TYPES[destination.id];
            if (!reportType) {
                continue;
            }
            items.push({
                kind: "report",
                id: `${reportingScopeKey(scope)}:${reportType}`,
                scope,
                reportType,
                label: destination.label,
                description: destination.description,
                url: destination.url,
            });
        }
    }

    const businessEntities = entities.filter(isEligibleReportingEntity);

    for (const entity of businessEntities) {
        const scope: ReportingScope = {
            kind: "entity",
            id: entity.id,
            label: entity.name ?? "Entity",
        };
        const scopeKey = reportingScopeKey(scope);
        for (const report of entityReports) {
            items.push({
                kind: "report",
                id: `${scopeKey}:${report.catalogId ?? report.reportType}`,
                scope,
                reportType: report.reportType,
                catalogId: report.catalogId,
                label: resolveEntityReportLabel(report),
                description: report.description,
                url: buildEntityReportUrl({
                    report,
                    entityId: entity.id,
                    startDate,
                    endDate,
                }),
            });
        }
        items.push(
            {
                kind: "taxPackage",
                id: `${scopeKey}:tax-package`,
                scope,
                label: TAX_PACKAGE_LABEL,
                description: TAX_PACKAGE_DESCRIPTION,
            },
            {
                kind: "reportPackage",
                id: `${scopeKey}:report-package`,
                scope,
                label: REPORT_PACKAGE_LABEL,
                description: REPORT_PACKAGE_DESCRIPTION,
            },
        );
    }

    return items;
}
